import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// Pages
import { WelcomePage } from '@/pages/WelcomePage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { JobDetailPage } from '@/pages/JobDetailPage';
import { ContractsPage } from '@/pages/ContractsPage';
import { ContractDetailPage } from '@/pages/ContractDetailPage';
import { GateRulesPage } from '@/pages/GateRulesPage';
import { KnowledgeBasePage } from '@/pages/KnowledgeBasePage';
import { AuditTrailPage } from '@/pages/AuditTrailPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { EscalationsPage } from '@/pages/escalations/EscalationsPage';
import { FEATURE_ESCALATIONS } from '@/lib/features';

/**
 * Root router.
 *
 * Route table (matches CLAUDE.md page registry):
 *   /                — public          — WelcomePage (redirects to /dashboard if signed in)
 *   /auth            — public          — LoginPage
 *   /dashboard       — protected       — DashboardPage
 *   (Submit Contract is now a dialog on Dashboard + Contracts, not a route.)
 *   /jobs/:id        — protected       — JobDetailPage
 *   /contracts       — protected       — ContractsPage
 *   /contracts/:id   — protected       — ContractDetailPage
 *   /audit           — protected       — AuditTrailPage
 *   /rules           — admin           — GateRulesPage
 *   /kb              — admin           — KnowledgeBasePage
 *   /settings        — admin           — SettingsPage
 */
export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          {/* Public */}
          <Route path="/" element={<WelcomePage />} />
          <Route path="/auth" element={<LoginPage />} />

          {/* Protected — wrapped in AppLayout via nested routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/jobs/:id" element={<JobDetailPage />} />
            <Route path="/contracts" element={<ContractsPage />} />
            <Route path="/contracts/:id" element={<ContractDetailPage />} />
            <Route path="/audit" element={<AuditTrailPage />} />
            {FEATURE_ESCALATIONS && (
              <Route path="/escalations" element={<EscalationsPage />} />
            )}

            {/* Admin-only routes */}
            <Route
              path="/rules"
              element={
                <AdminRoute>
                  <GateRulesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/kb"
              element={
                <AdminRoute>
                  <KnowledgeBasePage />
                </AdminRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <AdminRoute>
                  <SettingsPage />
                </AdminRoute>
              }
            />
          </Route>

          {/* Fallback — unknown route → home (Welcome auto-redirects if signed in) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
